// Categorical filtering for an in-memory table — "show me only these kinds".
// Promoted verbatim from admin's `lib/table-filter.ts` (the pattern is
// proven, zero admin-specific coupling) for reuse by a future BI table chart.
//
// The sibling of table-sort.ts, and it exists for the same reason: every
// list here already holds its rows in memory, so narrowing one is a local
// recompute rather than a refetch. What it adds is the question sorting cannot
// answer. Sorting says which row is biggest; a reader scanning a worklist
// wants "just the agent errors", "just the stopped ones", "just Israel and
// Ukraine" — and no amount of clicking a header substitutes for that. A column
// whose values repeat gets a filter; a name, a date, or a money figure does not
// (the search box and the sort caret already serve those).
//
// A column is declared as a value EXTRACTOR, exactly as a sort key is, so it can
// filter on something other than what it prints. Four rules live here rather
// than in each page, because getting any of them wrong quietly lies to the reader:
//
//   * OPTIONS COME FROM THE ROWS, not a hard-coded list. The backend can emit a
//     value before this bundle hears about it, and an option list that omits it
//     hides those rows behind a filter nobody can see, let alone clear. Pass
//     `options` only for a vocabulary this bundle OWNS (a boolean it derives).
//   * A ROW WITH NO VALUE GETS ITS OWN OPTION, never a silent drop. "Unknown" is
//     a real answer, and a coverage gap a reader can select is a gap somebody
//     investigates.
//   * A SELECTED VALUE IS ALWAYS LISTED, even when no row currently carries it
//     (a stale deep link, a refetch that dropped the last such row). Otherwise
//     the one control that would clear the empty table is the one control the
//     popover does not offer.
//   * WITHIN a column the selection is OR, ACROSS columns it is AND. That is
//     what people mean by ticking two boxes in one list and one in another.
//
// Callers declare their columns at MODULE scope where they can. A column that
// closes over component state (a URL-controlled selection, an option list with
// live counts) must be wrapped in `useMemo`, or every render rebuilds the
// options.
import { useCallback, useMemo } from "react";

import type { MultiSelectOption } from "./multi-select";
import { useRestoredState } from "./view-state";

/** The option a row lands on when it carries no value for a dimension. Distinct
 *  enough that it cannot collide with a value off the wire. */
export const NO_VALUE = "__no_value__";

/** What a column filters on. An ARRAY where one row can hold several values at
 *  once (a project's open alert reasons); null/undefined/"" mean "no value". */
export type FilterValue = string | readonly string[] | null | undefined;

export interface FilterColumn<T> {
  /** Stable id, unique within the table. Keys the selection and the control. */
  key: string;
  /** The control's accessible name, e.g. "Filter by stage". */
  label: string;
  /** Trigger text while nothing is picked, e.g. "All stages". */
  allLabel: string;
  /** This row's value(s) for the dimension. */
  value: (row: T) => FilterValue;
  /** How a raw value prints as an option. Defaults to the value itself. */
  optionLabel?: (value: string) => string;
  /** What the no-value option is called. Defaults to "Unknown". */
  noValueLabel?: string;
  /** Fixed option ORDER — a ladder, a severity run. Values outside it follow,
   *  ordered by label. Without it options are alphabetical by label. */
  order?: readonly string[];
  /** A CLOSED vocabulary, listed whether or not a row currently uses it. Only
   *  for a set this bundle owns, so a value that arrives off the wire can never
   *  go missing; leave it unset and the options are derived from the rows. */
  options?: readonly MultiSelectOption[];
  /** The STORE holds rows with no value for this column even when none are
   *  loaded. A server-filtered list can be narrowed so every loaded row has a
   *  value while unrecorded rows still exist behind the filter; a server facet
   *  sets this so the no-value bucket stays selectable. */
  hasNoValue?: boolean;
  /** Selection owned by the CALLER (a URL param) rather than by the hook.
   *  `onChange` is what makes the column controlled: without a way to write the
   *  selection back, a `selected` the hook honoured would pin the column open
   *  forever, so it is ignored. */
  selected?: readonly string[];
  onChange?: (next: readonly string[]) => void;
}

/** Column key → the values picked for it. Absent or empty means "no filter". */
export type FilterSelection = Readonly<Record<string, readonly string[]>>;

const EMPTY: readonly string[] = [];

/** One row's values for a column, with blanks dropped. */
function valuesOf(raw: FilterValue): readonly string[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw === "string") return raw === "" ? [] : [raw];
  return raw.filter((v) => v !== "");
}

/**
 * The options a column offers over `rows`.
 *
 * `selected` is folded in so a value that no row currently carries still gets a
 * checkbox — that is the only way to untick a stale deep link from the popover.
 */
export function columnOptions<T>(
  rows: readonly T[],
  column: FilterColumn<T>,
  selected: readonly string[] = EMPTY,
): MultiSelectOption[] {
  const label = column.optionLabel ?? ((value: string) => value);
  const order = column.order ?? EMPTY;
  const rank = (value: string): number => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };

  const present = new Set<string>();
  let anyMissing = false;
  for (const row of rows) {
    const values = valuesOf(column.value(row));
    if (values.length === 0) {
      anyMissing = true;
      continue;
    }
    for (const value of values) present.add(value);
  }

  // A closed vocabulary is listed verbatim; a derived one is whatever the rows
  // actually hold. Either way a selected value is never dropped.
  const listed = column.options
    ? column.options.map((o) => o.value)
    : [...present].sort(
        (a, b) =>
          rank(a) - rank(b) ||
          label(a).localeCompare(label(b), undefined, { sensitivity: "base", numeric: true }),
      );
  const extras = selected.filter((v) => v !== NO_VALUE && !listed.includes(v));

  const options: MultiSelectOption[] = column.options
    ? [...column.options, ...extras.map((value) => ({ value, label: label(value) }))]
    : [...listed, ...extras].map((value) => ({ value, label: label(value) }));

  // Always last: it is the absence bucket, not a peer of the real values.
  if (anyMissing || column.hasNoValue || selected.includes(NO_VALUE)) {
    options.push({ value: NO_VALUE, label: column.noValueLabel ?? "Unknown" });
  }
  return options;
}

/**
 * Options for a SERVER-FACETED column: the store's whole vocabulary (which the
 * backend derived), not the loaded rows'. Ordered exactly like the row-derived
 * options above — `order` first, then alphabetical by label — so a column reads
 * the same whether its options came from the rows or from a facet.
 *
 * This exists for the keyset-paged lists, whose filters run server-side: once a
 * selection narrows the ROWS, deriving options from them would collapse the
 * popover to the selected values, and every other option would become
 * impossible to tick.
 */
export function facetOptions(
  values: readonly string[],
  label: (value: string) => string = (value) => value,
  order: readonly string[] = [],
): MultiSelectOption[] {
  const rank = (value: string): number => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };
  return [...new Set(values)]
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        label(a).localeCompare(label(b), undefined, { sensitivity: "base", numeric: true }),
    )
    .map((value) => ({ value, label: label(value) }));
}

/** Does one row survive one column's selection? An empty selection passes. */
export function rowMatchesColumn<T>(
  row: T,
  column: FilterColumn<T>,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const values = valuesOf(column.value(row));
  if (values.length === 0) return selected.includes(NO_VALUE);
  return values.some((value) => selected.includes(value));
}

/** `rows` narrowed by every column: OR within a column, AND across them. */
export function filterRows<T>(
  rows: readonly T[],
  columns: readonly FilterColumn<T>[],
  selection: FilterSelection,
): T[] {
  return rows.filter((row) =>
    columns.every((column) => rowMatchesColumn(row, column, selection[column.key] ?? EMPTY)),
  );
}

/**
 * Is this control worth rendering?
 *
 * A control whose only option is the value every row already has cannot narrow
 * anything, so it is noise above a small table. It stays visible while
 * something is selected, or a filter would become impossible to clear the
 * moment it emptied the list down to one kind of row.
 */
export function isUsefulControl(optionCount: number, selectedCount: number): boolean {
  return optionCount > 1 || selectedCount > 0;
}

/** One rendered filter control. Shape of the props `<MultiSelect>` wants. */
export interface ColumnFilterControl {
  key: string;
  label: string;
  allLabel: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (next: readonly string[]) => void;
}

export interface UseColumnFiltersResult<T> {
  /** The rows that survive every column's selection. */
  rows: T[];
  /** The controls to render, minus the ones that could not narrow anything. */
  controls: readonly ColumnFilterControl[];
  /** Is at least one column narrowing the list right now? */
  active: boolean;
  /** Drop every selection, controlled columns included. */
  clear: () => void;
}

export interface ColumnFiltersOptions {
  /** Opt the selections this hook OWNS into surviving a history BACK hop, under a
   *  key unique across the app (`view-state.ts`). A column that brought its own
   *  `selected`/`onChange` is unaffected: it already lives in the URL, which
   *  survives back/forward on its own. */
  restore?: string;
}

/** The hook's own starting selection. A module constant so the identity is stable
 *  across renders even though only the first one reads it. */
const NO_SELECTION: FilterSelection = {};

/**
 * Filter `rows` by whichever values the reader ticked.
 *
 * The hook owns each column's selection unless the column brought its own
 * (`selected` + `onChange`), which is how a filter can live in the URL and stay
 * a link somebody can send.
 */
export function useColumnFilters<T>(
  rows: readonly T[],
  columns: readonly FilterColumn<T>[],
  options: ColumnFiltersOptions = {},
): UseColumnFiltersResult<T> {
  const [own, setOwn] = useRestoredState<FilterSelection>(options.restore, NO_SELECTION);

  const selection = useMemo<FilterSelection>(() => {
    const next: Record<string, readonly string[]> = {};
    for (const column of columns) {
      next[column.key] = column.onChange ? (column.selected ?? EMPTY) : (own[column.key] ?? EMPTY);
    }
    return next;
  }, [columns, own]);

  const filtered = useMemo(() => filterRows(rows, columns, selection), [rows, columns, selection]);

  const controls = useMemo<readonly ColumnFilterControl[]>(() => {
    const built: ColumnFilterControl[] = [];
    for (const column of columns) {
      const selected = selection[column.key] ?? EMPTY;
      // Options come from the WHOLE input, never from `filtered`: derived from
      // what survives, every other value would vanish from the popover the
      // instant one was ticked, and un-ticking it would be impossible.
      const options = columnOptions(rows, column, selected);
      if (!isUsefulControl(options.length, selected.length)) continue;
      built.push({
        key: column.key,
        label: column.label,
        allLabel: column.allLabel,
        options,
        selected,
        onChange: (next) => {
          if (column.onChange) {
            column.onChange(next);
            return;
          }
          setOwn((prev) => ({ ...prev, [column.key]: next }));
        },
      });
    }
    return built;
    // `setOwn` is a stable `useState` setter, listed because it now arrives
    // through a wrapper hook where eslint cannot see that.
  }, [rows, columns, selection, setOwn]);

  const active = useMemo(
    () => columns.some((column) => (selection[column.key] ?? EMPTY).length > 0),
    [columns, selection],
  );

  const clear = useCallback(() => {
    for (const column of columns) {
      if (column.onChange && (column.selected ?? EMPTY).length > 0) column.onChange(EMPTY);
    }
    setOwn(NO_SELECTION);
  }, [columns, setOwn]);

  return { rows: filtered, controls, active, clear };
}
